import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import QuillText from 'packages/kakapo-ui-kit/components/editors/QuillText';
import Paragraph from 'packages/kakapo-ui-kit/components/common/Paragraph';
import { GeometryObserver } from '@treams/treams-ui';
import { Popover } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

const Bullet = styled(Flex)<{
	filled?: boolean;
	enabled?: boolean;
	editable?: boolean;
}>`
	width: 32px;
	height: 32px;
	border-radius: 16px;
	overflow: hidden;

	overflow: hidden;

	${(props) =>
		props.editable &&
		!props.filled &&
		`
			border: 1px solid #dae0e8;
		`}

	${(props) =>
		!props.filled &&
		!props.editable &&
		`
				transform: scale(0.25, 0.25);
				background: #dae0e8;
		`}
	${(props) => props.enabled === false && `opacity: 0.25;`}
`;

const Dot = styled.div<{ editable?: boolean; filled?: boolean }>`
	position: absolute;
	left: 12px;
	top: 12px;
	width: 8px;
	height: 8px;
	border-radius: 8px;
	background: ${(props) => props.editable && 'rgba(0, 0, 0, 0.1)'};
	background: ${(props) => !props.editable && props.filled && 'white'};
`;

const Fill = styled.div<{ party: graphql.ConversationRole }>`
	width: 32px;
	height: 32px;
	background: ${(props) =>
		props.party === graphql.ConversationRole.Requester
			? props.theme.colors.employee
			: props.theme.colors.manager};
`;

const Label = styled.div`
	color: ${(props) => props.theme.colors.black};
	text-overflow: ellipsis;
	overflow: hidden;
`;

const TooltipTitle = styled.div`
	border-top: 1px solid ${(props) => props.theme.colors.lightBlue};
	padding-top: 10px;
	margin: 0px -16px;
	margin-top: 10px !important;
	margin-bottom: -5px;
	text-align: center;
`;

function Level(props: {
	active: boolean;
	level: {
		enabled: boolean;
		criteria?: graphql.LocalizedString;
		level: {
			title: graphql.LocalizedString;
		};
	};
	role: graphql.ConversationRole;
	onClick?: () => void;
	editable?: boolean;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();

	const [opened, { close, open }] = useDisclosure(false);
	const isSelected = props.active;

	return (
		<Popover position="top" width="300px" withArrow shadow="xs" opened={opened}>
			<Popover.Target>
				<Box
					style={{
						position: 'relative',
						cursor: props.editable && props.level.enabled && !isSelected ? 'pointer' : 'default',
					}}
					onClick={() => props.level.enabled && props.onClick?.()}
					onMouseEnter={open}
					onMouseLeave={close}
				>
					<Bullet editable={props.editable} filled={isSelected} enabled={props.level.enabled}>
						{isSelected && <Fill party={props.role} />}
						<Dot editable={props.editable} filled={isSelected} />
					</Bullet>
				</Box>
			</Popover.Target>
			<Popover.Dropdown>
				<Paragraph color="slateBlue">
					<QuillText
						quill={localize(props.level.criteria ?? [])?.text}
						placeholder={t('evaluation.submission.competency.levels.noDescription')}
					/>
				</Paragraph>
				<TooltipTitle>{localize(props.level.level.title)?.text}</TooltipTitle>
			</Popover.Dropdown>
		</Popover>
	);
}

function Levels({
	levels,
	role,
	score,
	onChange,
	editable,
}: {
	levels: { enabled: boolean; level: { title: graphql.LocalizedString } }[];
	role: graphql.ConversationRole;
	score: number | null | undefined;
	onChange?: (value: number) => void;
	editable?: boolean;
}) {
	return (
		<>
			{levels.map((currentLevel, index) => {
				// Filter bullets in this map, not before. This to preserve
				// the correct active index.
				if (!currentLevel.enabled) {
					return null;
				}

				return (
					<Level
						key={index}
						onClick={() => onChange?.(index)}
						level={currentLevel}
						active={score === index}
						role={role}
						editable={editable}
					/>
				);
			})}
		</>
	);
}

function LeftLabel({
	levels,
}: {
	levels: { enabled: boolean; level: { title: graphql.LocalizedString } }[];
}) {
	const { localize } = useLocalize();

	const enabledLevels = levels.filter((level) => Boolean(level.enabled));

	return (
		<Box>
			<Label>{localize(enabledLevels[0].level.title).text}</Label>
		</Box>
	);
}

function RightLabel({
	levels,
}: {
	levels: { enabled: boolean; level: { title: graphql.LocalizedString } }[];
}) {
	const { localize } = useLocalize();

	const enabledLevels = levels.filter((level) => Boolean(level.enabled));

	return (
		<Box>
			<Label>{localize(enabledLevels[enabledLevels.length - 1].level.title).text}</Label>
		</Box>
	);
}

export default function DotScore(props: {
	levels: {
		enabled: boolean;
		level: {
			title: graphql.LocalizedString;
		};
	}[];
	role: graphql.ConversationRole;
	score: number | null | undefined;
	labels?: boolean;
	onChange?: (value: number) => void;
	editable?: boolean;
}) {
	const ref = useRef(null);

	return (
		<Box ref={ref} padding="8px">
			<GeometryObserver innerRef={ref}>
				{(bounds) =>
					bounds.width < 320 ? (
						<Box>
							<Flex alignItems="center" justifyContent="space-between">
								<Levels
									onChange={props.onChange}
									levels={props.levels}
									role={props.role}
									score={props.score}
									editable={props.editable}
								/>
							</Flex>
							<Flex justifyContent={'space-between'}>
								<LeftLabel levels={props.levels} />
								<RightLabel levels={props.levels} />
							</Flex>
						</Box>
					) : (
						<Flex alignItems="center" justifyContent="space-between">
							<LeftLabel levels={props.levels} />
							<Levels
								onChange={props.onChange}
								levels={props.levels}
								role={props.role}
								score={props.score}
								editable={props.editable}
							/>
							<RightLabel levels={props.levels} />
						</Flex>
					)
				}
			</GeometryObserver>
		</Box>
	);
}
